import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { LntInput, LntSelect } from '@app/components/base'
import { TopFilters as TopFiltersWrapper } from '@app/components'

import { getSubtypesTypes } from '@app/api/routes/monitorings'

import { MONITORINGS_SUBTYPES_KEY } from '@app/constants/queryKeys'
import { FILTERS_PLACEHOLDERS } from '@app/constants/filters'
import { FILTERS_PARAMS_KEYS } from '@app/api/constants'
import { SIZE } from '@app/constants'

import { useFiltersSearch } from '@app/hooks'

import { getFilterFromEntities } from '@app/utils/entitiesAdapters'
import { stringRegexReplace } from '@app/utils/string'

import { commonSelectProps, statusOptions } from './configs'

import type { ChangeEvent } from 'react'
import type { FiltersParams } from '@app/types/filter'
import type { TopFilterProps } from './types'

const updateFiltersByKey = (prev: FiltersParams | null, key: string, value: unknown): object => {
  if (!prev) return { [key]: value }
  return { ...prev, [key]: value }
}

export const TopFilters: React.FC<TopFilterProps> = ({ onSearch, initialFilters, monitoringTypeId }) => {
  const {
    filters,
    setFilters,
    canResetFilters,
    handleClearFilters: clearFilters,
    handleSearch: submitFilters,
    handleSelectFilterChange
  } = useFiltersSearch({ initialFilters, onSearch })

  const [parsedProductCodeValue, setParsedProductCodeValue] = useState(filters?.productCode)

  const { data: subtypesTypesData, isLoading: isLoadingSubtypesTypesData } = useQuery({
    enabled: !!monitoringTypeId,
    queryKey: [MONITORINGS_SUBTYPES_KEY.subtypesItems, monitoringTypeId],
    queryFn: async () => await getSubtypesTypes({ monitoringTypeId })
  })

  const subtypesTypesDataOptions = useMemo(() => getFilterFromEntities(subtypesTypesData), [subtypesTypesData])

  const onChangeInputProductCode = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value

    setParsedProductCodeValue(value)

    const REGEXP_PATTERN = /\D+/g
    const parsedString = stringRegexReplace(value, REGEXP_PATTERN)

    setFilters(prev => updateFiltersByKey(prev, FILTERS_PARAMS_KEYS.productCode, parsedString))
  }

  const handleClearFilters = (): void => {
    clearFilters()
    setParsedProductCodeValue('')
  }

  const handleSearch = (): void => {
    submitFilters()
    setParsedProductCodeValue(filters?.productCode)
  }

  return (
    <TopFiltersWrapper
      canReset={ canResetFilters }
      onClear={ handleClearFilters }
      onSearch={ handleSearch }
    >
      <LntInput
        value={ parsedProductCodeValue }
        onChange={ event => onChangeInputProductCode(event) }
        placeholder={ FILTERS_PLACEHOLDERS.productCode }
        size={ SIZE.middle }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.monitoringSubtypeIds }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.monitoringSubtypeIds, value) }
        placeholder={ FILTERS_PLACEHOLDERS.monitoringsSubtypesFilter }
        loading={ isLoadingSubtypesTypesData }
        options={ subtypesTypesDataOptions }
      />

      <LntSelect
        { ...commonSelectProps }
        value={ filters.status }
        options={ statusOptions }
        onChange={ value => handleSelectFilterChange(FILTERS_PARAMS_KEYS.status, value) }
        placeholder={ FILTERS_PLACEHOLDERS.productStatus }
      />
    </TopFiltersWrapper>
  )
}
